import {
  Component,
  DestroyRef,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { evaluateFormula, getFormulaRelevantIds } from '../../../../utils/formulaTransformers/parseFormulas';
import { combineLatest, map, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseItemProperties } from './base-item-properties';
import { extractXmlProps, UserInformation, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import { EditorSettings } from '../../types/editor-settings';

// Explicit interface for BaseItemComponent public API - for better IDE support
export interface IBaseItem<T> {
  xml: InputSignal<ChildNode>;
  form: InputSignal<FormGroup>;
  user: InputSignal<UserInformation>;
  settings: InputSignal<EditorSettings>;
  sectionItemId: InputSignal<string>;
  firstSiblingItemId: InputSignal<string>;
  renderedXmlItems: InputSignal<ChildNode[]>;
  renderedXmlItemsQty: InputSignal<number>;
  isReadonly: InputSignal<boolean>;
  properties: BaseItemProperties & T;
  sharedFirstColumnWidths: WritableSignal<Record<string, number>>;
  isVisible: WritableSignal<boolean>;
  isGenericRepeatItem: WritableSignal<boolean>;
  visibilityChanged: OutputEmitterRef<boolean>;
  htmlId: () => string;
  currentId: () => string;
  getXML: () => string;
  extractLocalProperties: (xml: Element) => BaseItemProperties & T;
}

@Component({
  template: '',
  standalone: false
})
export abstract class BaseItemComponent<T> implements OnInit {
  readonly xml = input.required<ChildNode>();
  readonly form = input.required<FormGroup>();
  readonly user = input.required<UserInformation>();
  readonly settings = input.required<EditorSettings>();
  readonly sectionItemId = input.required<string>();
  readonly firstSiblingItemId = input.required<string>();
  readonly renderedXmlItems = input.required<ChildNode[]>();
  readonly renderedXmlItemsQty = input.required<number>();
  readonly isReadonly = input.required<boolean>();

  abstract properties: BaseItemProperties & T;

  sharedFirstColumnWidths = signal<Record<string, number>>({});
  public isVisible = signal(true);
  isGenericRepeatItem = signal(false);
  readonly visibilityChanged = output<boolean>();

  protected readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.properties = this.extractLocalProperties(this.xml() as Element);
    this.isGenericRepeatItem.set(this.properties.isGenericRepeatItem);
    this.isVisible.set(!this.isGenericRepeatItem());
    if (this.properties.isGenericRepeatItem) {
      this.visibilityChanged.emit(this.isVisible());
    }
    this.setupActivationRule();
  }

  getXML(): string {
    return xmlElementToString(this.xml()) || '';
  }

  abstract extractLocalProperties(xml: Element): BaseItemProperties & T;

  abstract htmlId(): string;

  abstract currentId(): string;

  private setupActivationRule(): void {
    if (!this.properties.activationRule) {
      return;
    }

    const ids = getFormulaRelevantIds(this.properties.activationRule);

    if (ids.length === 0) {
      // Constant rule: evaluate once instead of leaving the item permanently visible.
      this.onActivationRuleEvaluated(evaluateFormula(this.properties.activationRule, []));
      return;
    }

    const resolved = ids
      .map((id) => ({ id, control: this.form().get(id) }))
      .filter((entry): entry is { id: string; control: AbstractControl } => !!entry.control);

    // Unresolved means the rule references an item below this one, which is invalid input, not a
    // timing problem. Hiding matches the desktop's IFERROR(..., DBNULL()) result.
    if (resolved.length === 0) {
      const props = extractXmlProps(this.xml());
      const label = props['Text'] || props['InputCaptionLeft'] || props['Text1'] || '';
      console.warn(
        '[LiveFormsEditor] ActivationRule never runs, referenced item must be above this one:',
        this.properties.activationRule,
        'unresolved:',
        ids.join(', '),
        'item:',
        `${this.xml().nodeName}${label ? ` "${label}"` : ''}`,
        this.properties.customId || this.properties.id
      );
      this.onActivationRuleEvaluated(false);
      return;
    }

    const streams = resolved.map(({ id, control }) =>
      control.valueChanges.pipe(
        startWith(control.value),
        map((value) => ({ id, control, value }))
      )
    );

    combineLatest(streams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((controlData) => {
        const result = evaluateFormula(this.properties.activationRule, controlData);
        this.onActivationRuleEvaluated(result);
      });
  }

  private onActivationRuleEvaluated(result: any): void {
    const newVisibility = !!result;
    const currentVisibility = this.isVisible();
    const effectiveVisibility = !this.isGenericRepeatItem() && newVisibility;

    if (effectiveVisibility !== currentVisibility) {
      this.isVisible.set(effectiveVisibility);
      this.visibilityChanged.emit(effectiveVisibility);
    }
  }
}
